import React from 'react';
import PropTypes from 'prop-types';
import dateFormat from 'dateformat';
import { defaultTo } from 'lodash';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { getCpoPath } from 'client/data/models/cpo';
import { Link } from 'site-modules/shared/components/link/link';
import { HashAnchor } from 'site-modules/shared/components/hash-anchor/hash-anchor';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';

export function CpoEditorialArticle({ content, index }) {
  const { title } = content;
  const { dateModified, datePublished, authorName, authorPageUrl } = content.getAllMetadata();
  const articleDate = defaultTo(dateModified, datePublished);
  const hasArticleMetadata = !!authorName;
  const name = `cpo-editorial-article${index ? `-${index}` : ''}`;
  const paragraphs = content.children();

  return (
    <div className="p-1_5 bg-cool-gray-100 rounded-12">
      <HashAnchor name={name} />
      <ScrollElement id={name} />
      <h2 className="heading-3">{title}</h2>
      {hasArticleMetadata && (
        <div className="d-flex align-items-center">
          <p className="size-14 mb-0_5">
            by{' '}
            <Link to={authorPageUrl} className="text-primary-darker">
              {authorName}
            </Link>
            , {dateFormat(articleDate, 'mmmm d, yyyy')}
          </p>
        </div>
      )}
      {paragraphs.map(paragraph =>
        paragraph.metadata('collapsed').boolean() ? (
          <ContentCollapse
            key={paragraph.id}
            btnContainerClasses="py-0 px-0_5 mt-0_5 mb-1"
            btnClasses="p-0 text-primary-darker read-more-btn"
            btnTextClasses="font-weight-normal"
            btnSize="lg"
            btnArrowClasses="xsmall"
            justify="justify-content-start"
            textClosed="Read more"
            textOpen="Read less"
            showBtnBelow
          >
            <ContentFragment>{paragraph.metadata('htmlContent').value()}</ContentFragment>
          </ContentCollapse>
        ) : (
          <ContentFragment key={paragraph.id}>{paragraph.metadata('htmlContent').value()}</ContentFragment>
        )
      )}
    </div>
  );
}

CpoEditorialArticle.propTypes = {
  content: CmsEntities.Content,
  index: PropTypes.number,
};

CpoEditorialArticle.defaultProps = {
  content: DEFAULT_CONTENT,
  index: 0,
};

export function CpoEditorialArticlesUI({ content, isMobile, className }) {
  const articles = content.children();

  return (
    <Row className="mt-3">
      <Col xs={12} md={7} lg={8}>
        {articles.map((articleContent, index) => (
          <CpoEditorialArticle
            key={articleContent.id}
            content={articleContent}
            index={index}
            isMobile={isMobile}
            className={className}
          />
        ))}
      </Col>
      {!isMobile && (
        <Col xs={12} md={5} lg={4}>
          <NativeAd
            adNameWired="mrect"
            adNameMobile="mmrect"
            nativeStyle="mediumrectangle"
            position={'1'}
            renderWhenViewable={false}
            refreshable={false}
            all
            showLabel
            useMinHeight
            wrapperClass="mb-3"
          />
          <NativeAd
            adNameWired="mrect"
            adNameMobile="mmrect"
            nativeStyle="mediumrectangle"
            position={'2'}
            renderWhenViewable={false}
            refreshable={false}
            all
            showLabel
            useMinHeight
          />
        </Col>
      )}
    </Row>
  );
}

CpoEditorialArticlesUI.propTypes = {
  content: CmsEntities.Content,
  isMobile: PropTypes.bool,
  className: PropTypes.string,
};

CpoEditorialArticlesUI.defaultProps = {
  content: DEFAULT_CONTENT,
  isMobile: false,
  className: null,
};

export const stateToPropsConfig = {
  content: bindToContent(getCpoPath(), 'cpo-editorial-articles'),
};

export const CpoEditorialArticles = connectToModel(CpoEditorialArticlesUI, stateToPropsConfig);
